import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { adminGroupActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const groupImportApi = createImportApi({
    actions: adminGroupActions,
    capabilities,
    endpoint: 'admin/groups',
    templateFileName: 'GroupImportTpl'
});
export const ApiGroupsImportCollection = groupImportApi.importCollection;
export const ApiGroupsDownloadImportModelItem = groupImportApi.downloadImportModel;

export const ApiGroupsGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {},
    updateStore = true
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }
    await gestopAPI
        .post('admin/groups/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) => {
            if (updateStore) {
                dispatch(adminGroupActions.setList(response?.data?.list));
                dispatch(adminGroupActions.setCount(response?.data?.count));
            }
            if (capabilities.app.includes('loader')) {
                dispatch(adminGroupActions.stopLoading());
            }
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.list);
            }
        })
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('admin/groups/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiGroupsPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .post('admin/groups', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .post('admin/groups/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(adminGroupActions.startExporting());
    }

    await gestopAPI
        .post('admin/groups/export', { exportType, format, columns, filters, orderBy, groupBy }, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(adminGroupActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(adminGroupActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .put(`admin/groups/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .patch(`admin/groups/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .post(`admin/groups/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .post(`admin/groups/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(adminGroupActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiGroupsGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(adminGroupActions.startLoading());
    }

    await gestopAPI
        .get(`admin/groups/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(adminGroupActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(adminGroupActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
